import { useEffect, useMemo, useState } from 'react'
import { loadUserById } from '@domain/features/user/user-feature'
import type { UserProfile } from '@domain/models/user'

function fallbackDisplayName(userId: string | undefined): string {
  if (!userId) {
    return 'Unknown user'
  }
  return `User ${userId.slice(0, 6)}`
}

function normalizeName(profile: UserProfile): string | undefined {
  const fullName = profile.fullName?.trim()
  if (fullName) {
    return fullName
  }

  const profileSlug = profile.profile_slug?.trim()
  if (profileSlug) {
    return profileSlug
  }

  return undefined
}

export function useUserProfiles(userIds: Array<string | undefined>) {
  const [profilesById, setProfilesById] = useState<Record<string, UserProfile>>({})

  const normalizedIds = useMemo(() => {
    const ids = new Set<string>()
    userIds.forEach((userId) => {
      const normalizedId = userId?.trim()
      if (normalizedId) {
        ids.add(normalizedId)
      }
    })
    return Array.from(ids)
  }, [userIds])

  useEffect(() => {
    const unresolvedIds = normalizedIds.filter((userId) => !(userId in profilesById))
    if (unresolvedIds.length === 0) {
      return
    }

    let cancelled = false

    const loadProfiles = async () => {
      const loadedProfiles = await Promise.all(
        unresolvedIds.map(async (userId) => {
          try {
            const profile = await loadUserById(userId)
            return [userId, profile] as const
          } catch {
            return [userId, null] as const
          }
        }),
      )

      if (cancelled) {
        return
      }

      setProfilesById((previousMap) => {
        const nextMap = { ...previousMap }
        loadedProfiles.forEach(([userId, profile]) => {
          if (profile) {
            nextMap[userId] = profile
          }
        })
        return nextMap
      })
    }

    void loadProfiles()

    return () => {
      cancelled = true
    }
  }, [normalizedIds, profilesById])

  const resolveDisplayName = (userId: string | undefined): string => {
    if (!userId) {
      return 'Unknown user'
    }
    const profile = profilesById[userId]
    return profile ? normalizeName(profile) ?? fallbackDisplayName(userId) : fallbackDisplayName(userId)
  }

  const resolveAvatarUrl = (userId: string | undefined): string | undefined => {
    if (!userId) {
      return undefined
    }
    const profile = profilesById[userId]
    const avatarUrl = profile?.avatarUrl?.trim()
    if (avatarUrl) {
      return avatarUrl
    }

    const photoUrl = profile?.photo_url?.trim()
    return photoUrl || undefined
  }

  return {
    resolveDisplayName,
    resolveAvatarUrl,
  }
}
